import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import Nav from './components/Nav';
import Search from './components/Search';
import Footer from './components/Footer';
import Home from './pages/Home';
import GalleryPage from './pages/GalleryPage';
import Work from './pages/Work';
import About from './pages/About';
import FAQ from './pages/FAQ';
import NotFound from './pages/NotFound';
import { GALLERIES, galleryByPath } from './utils/galleries';
import { UiProvider } from './hooks/useUi';

const COVER_MS = 450;
const REVEAL_MS = 550;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const navVariantFor = (pathname) => {
  if (pathname === '/') return 'home';
  if (galleryByPath(pathname)) return 'detail';
  return 'solid';
};

/**
 * Keeps rendering the old route while a cream shutter fades in over it,
 * swaps routes underneath, then fades the shutter out. Query-string-only
 * changes (e.g. opening a photo) swap instantly with no shutter.
 */
const scrollPositions = new Map();

const Shell = () => {
  const location = useLocation();
  const navType = useNavigationType();
  const [shown, setShown] = useState(location);
  const [phase, setPhase] = useState('initial');
  const pendingScroll = useRef(null);

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    const id = setTimeout(() => setPhase('revealing'), reducedMotion() ? 0 : 650);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (location.pathname === shown.pathname) {
      if (location !== shown) setShown(location);
      return undefined;
    }
    const swap = () => {
      scrollPositions.set(shown.key, window.scrollY);
      pendingScroll.current = navType === 'POP' ? scrollPositions.get(location.key) || 0 : 0;
      setShown(location);
    };
    if (reducedMotion()) {
      swap();
      return undefined;
    }
    setPhase('covering');
    const id = setTimeout(() => {
      swap();
      setPhase('revealing');
    }, COVER_MS);
    return () => clearTimeout(id);
  }, [location, shown, navType]);

  // Scroll once the new page (and its measured grids) has laid out.
  useLayoutEffect(() => {
    if (pendingScroll.current === null) return undefined;
    const y = pendingScroll.current;
    pendingScroll.current = null;
    window.scrollTo(0, y);
    if (!y) return undefined;
    const id = requestAnimationFrame(() => window.scrollTo(0, y));
    return () => cancelAnimationFrame(id);
  }, [shown]);

  useEffect(() => {
    if (phase !== 'revealing') return undefined;
    const id = setTimeout(() => setPhase('idle'), REVEAL_MS);
    return () => clearTimeout(id);
  }, [phase]);

  const variant = navVariantFor(shown.pathname);

  return (
    <>
      <a className="skipLink" href="#main">
        Skip to content
      </a>
      <Nav variant={variant} />
      <Search />
      <div id="main" className="page">
        <Routes location={shown}>
          <Route path="/" element={<Home />} />
          <Route path="/work" element={<Work />} />
          <Route path="/travel" element={<Work preset="trip" />} />
          {GALLERIES.map((g) => (
            <Route key={g.slug} path={g.path} element={<GalleryPage key={g.slug} slug={g.slug} />} />
          ))}
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer />
      </div>
      <div className={`shutter shutter--${phase}`} aria-hidden="true">
        <span className="shutter__mark">Jonny Coe</span>
      </div>
    </>
  );
};

const App = () => (
  <Router>
    <UiProvider>
      <Shell />
    </UiProvider>
  </Router>
);

export default App;
